import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useTranslation } from 'react-i18next';
import { StatsPageInfo, StatCard, StatColor } from '~/components/Shared/StatsPageInfo/StatsPageInfo';

interface QuestionnairesStatistics {
  total: number | null;
  [key: string]: number | null;
}

interface QuestionnaireStatus {
  id: number;
  name: string;
  type: string;
}

interface Props {
  statusFilter: string;
  setStatusFilter: (value: string) => void;
}

const AllIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

const ActiveIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const CreatedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="16"/>
    <line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);

const ClosedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="15" y1="9" x2="9" y2="15"/>
    <line x1="9" y1="9" x2="15" y2="15"/>
  </svg>
);

const STATUS_COLORS: Record<string, StatColor> = {
  created: 'purple',
  active: 'green',
  closed: 'red',
  archived: 'gray',
};

const STATUS_ICONS: Record<string, React.ReactNode> = {
  created: <CreatedIcon />,
  active: <ActiveIcon />,
  closed: <ClosedIcon />,
  archived: <ClosedIcon />,
};

export const QuestionnairesPageInfo = ({ statusFilter, setStatusFilter }: Props) => {
  const { t } = useTranslation();

  const { data: stats } = useQuery<QuestionnairesStatistics>({
    queryFn: async () => {
      const response = await instance.get('admin/questionnaire-statistics');
      return response.data;
    },
    queryKey: ['questionnaire-statistics'],
  });

  const { data: statuses } = useQuery<QuestionnaireStatus[]>({
    queryFn: async () => {
      const response = await instance.get('admin/questionnaires-status');
      return response.data;
    },
    queryKey: ['questionnaires-status'],
  });

  const statusCards: StatCard[] = (statuses ?? []).map(status => ({
    value: String(status.id),
    label: status.name,
    count: stats?.[status.type] ?? null,
    color: STATUS_COLORS[status.type] ?? 'orange',
    icon: STATUS_ICONS[status.type] ?? <CreatedIcon />,
  }));

  const cards: StatCard[] = [
    { value: '', label: t('total'), count: stats?.total, color: 'purple', icon: <AllIcon /> },
    ...statusCards,
  ];

  return (
    <StatsPageInfo
      activeStatus={statusFilter}
      onStatusChange={setStatusFilter}
      cards={cards}
    />
  );
};
